import { useCallback, useEffect, useState, type FormEvent } from 'react';

interface PendingItem {
  readonly id: string;
  readonly title: string;
  readonly severity: string;
  readonly responsible: { userId: string; name: string } | null;
  readonly dueAt: string | null;
}

export interface ShiftHandoffBoard {
  readonly members: readonly { userId: string; name: string; email: string }[];
  readonly operations: readonly {
    id: string;
    referenceCode: string;
    missionName: string | null;
    state: string;
    registrationMark: string | null;
    responsible: { userId: string; name: string } | null;
    nextEventAt: string | null;
    nextOrigin: string | null;
    nextDestination: string | null;
    pendingItems: readonly PendingItem[];
  }[];
  readonly latestHandoff: {
    id: string;
    fromName: string;
    toName: string;
    summary: string;
    createdAt: string;
    operationCount: number;
    openPendingCount: number;
    criticalPendingCount: number;
  } | null;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function responseError(response: Response): Error & { status: number } {
  const error = new Error(`REQUEST_FAILED:${response.status}`) as Error & { status: number };
  error.status = response.status;
  return error;
}

export function ShiftHandoffPanel({
  canEdit,
  organizationId,
  onExpiredSession,
  onOpenOperation,
}: {
  readonly canEdit: boolean;
  readonly organizationId: string;
  readonly onExpiredSession: () => Promise<void>;
  readonly onOpenOperation: (operationId: string) => void;
}) {
  const [board, setBoard] = useState<ShiftHandoffBoard>();
  const [error, setError] = useState<string>();
  const [feedback, setFeedback] = useState<string>();
  const [toUserId, setToUserId] = useState('');
  const [summary, setSummary] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadBoard = useCallback(async () => {
    const response = await fetch(`/v1/organizations/${organizationId}/shift-handoff`, {
      credentials: 'include',
    });
    if (response.status === 401) {
      await onExpiredSession();
      return undefined;
    }
    if (!response.ok) throw responseError(response);
    return (await response.json()) as ShiftHandoffBoard;
  }, [onExpiredSession, organizationId]);

  useEffect(() => {
    let active = true;
    setBoard(undefined);
    setError(undefined);
    void loadBoard()
      .then((result) => {
        if (!active || !result) return;
        setBoard(result);
        setToUserId((current) => current || result.members[0]?.userId || '');
      })
      .catch(() => {
        if (active) setError('Não foi possível carregar a passagem de turno.');
      });
    return () => {
      active = false;
    };
  }, [loadBoard]);

  const submitHandoff = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || !toUserId || !summary.trim()) return;
    setIsSaving(true);
    setError(undefined);
    setFeedback(undefined);
    try {
      const response = await fetch(`/v1/organizations/${organizationId}/shift-handoffs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handoffId: crypto.randomUUID(),
          idempotencyKey: crypto.randomUUID(),
          toUserId,
          summary,
        }),
      });
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw responseError(response);
      const refreshed = await loadBoard();
      if (refreshed) setBoard(refreshed);
      setSummary('');
      setFeedback('Passagem registrada com o retrato atual da operação.');
    } catch (requestError) {
      const status = (requestError as { status?: number }).status;
      setError(
        status === 409
          ? 'Este envio entrou em conflito. Atualize o quadro e tente novamente.'
          : status === 400
            ? 'Revise o destinatário e o resumo da passagem.'
            : 'Não foi possível registrar a passagem. Nenhum retrato parcial foi salvo.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const openItems = board?.operations.flatMap((operation) => operation.pendingItems) ?? [];
  const criticalCount = openItems.filter((item) => item.severity === 'critical').length;

  return (
    <section className="handoffPanel" id="shift-handoff" aria-labelledby="shift-handoff-title">
      <header className="cardHeader handoffHeader">
        <div>
          <span className="sectionEyebrow">Continuidade operacional</span>
          <h2 id="shift-handoff-title">Passagem de turno</h2>
          <p>Próximos horários, responsáveis e pendências abertas em um único quadro.</p>
        </div>
        {board ? (
          <div className="handoffTotals" aria-label="Resumo das pendências">
            <span>
              <strong>{board.operations.length}</strong> operações
            </span>
            <span>
              <strong>{openItems.length}</strong> pendências
            </span>
            <span className={criticalCount > 0 ? 'handoffCriticalTotal' : undefined}>
              <strong>{criticalCount}</strong> críticas
            </span>
          </div>
        ) : null}
      </header>

      {error ? (
        <div className="inlineError" role="alert">
          {error}
        </div>
      ) : null}
      {feedback ? (
        <div className="inlineSuccess" role="status">
          {feedback}
        </div>
      ) : null}
      {!board && !error ? (
        <div className="workflowLoading" role="status">
          Preparando o quadro do turno…
        </div>
      ) : null}

      {board ? (
        <>
          {board.latestHandoff ? (
            <article className="latestHandoff">
              <div>
                <span>Última passagem</span>
                <strong>
                  {board.latestHandoff.fromName} → {board.latestHandoff.toName}
                </strong>
                <small>{formatDateTime(board.latestHandoff.createdAt)}</small>
              </div>
              <p>{board.latestHandoff.summary}</p>
              <span>
                {board.latestHandoff.operationCount} operações ·{' '}
                {board.latestHandoff.openPendingCount} pendências no retrato
              </span>
            </article>
          ) : (
            <p className="handoffEmptyHistory">Ainda não há passagem registrada neste ambiente.</p>
          )}

          <div className="handoffOperationGrid">
            {board.operations.map((operation) => (
              <article className="handoffOperation" key={operation.id}>
                <header>
                  <div>
                    <span>{operation.referenceCode}</span>
                    <strong>{operation.missionName ?? 'Missão sem identificação'}</strong>
                  </div>
                  <button type="button" onClick={() => onOpenOperation(operation.id)}>
                    Abrir ficha
                  </button>
                </header>
                <dl>
                  <div>
                    <dt>Próximo horário</dt>
                    <dd>
                      {operation.nextEventAt
                        ? formatDateTime(operation.nextEventAt)
                        : 'Não informado'}
                    </dd>
                  </div>
                  <div>
                    <dt>Rota</dt>
                    <dd>
                      {operation.nextOrigin ?? '—'} → {operation.nextDestination ?? '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Responsável</dt>
                    <dd>{operation.responsible?.name ?? 'Não definido'}</dd>
                  </div>
                </dl>
                {operation.pendingItems.length > 0 ? (
                  <ul className="handoffPendingList">
                    {operation.pendingItems.map((item) => (
                      <li className={`pendingSeverity-${item.severity}`} key={item.id}>
                        <span>
                          {item.severity === 'critical'
                            ? 'Crítica'
                            : item.severity === 'warning'
                              ? 'Atenção'
                              : 'Informativa'}
                        </span>
                        <strong>{item.title}</strong>
                        <small>
                          {item.responsible?.name ?? 'Sem responsável'}
                          {item.dueAt ? ` · até ${formatDateTime(item.dueAt)}` : ''}
                        </small>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="handoffNoPending">Sem pendências abertas.</p>
                )}
              </article>
            ))}
          </div>

          {canEdit ? (
            <form className="handoffForm" onSubmit={(event) => void submitHandoff(event)}>
              <label>
                <span>Recebedor do turno</span>
                <select
                  required
                  value={toUserId}
                  onChange={(event) => setToUserId(event.target.value)}
                >
                  <option value="">Selecione</option>
                  {board.members.map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.name} · {member.email}
                    </option>
                  ))}
                </select>
              </label>
              <label className="handoffSummaryField">
                <span>Resumo para o próximo turno</span>
                <textarea
                  maxLength={2000}
                  minLength={3}
                  placeholder="Decisões tomadas, pontos de atenção e próximos contatos."
                  required
                  rows={3}
                  value={summary}
                  onChange={(event) => setSummary(event.target.value)}
                />
              </label>
              <button
                className="primaryButton"
                disabled={isSaving || board.operations.length === 0}
                type="submit"
              >
                {isSaving ? 'Registrando…' : 'Registrar passagem'}
              </button>
            </form>
          ) : (
            <p className="checklistReadOnly">
              Seu perfil permite consultar a passagem, sem registrar alterações.
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}
